import { useEffect, useState } from "react";
import api from "@/lib/api";
import { DashboardShell } from "@/components/DashboardShell";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from "recharts";
import {
  IndianRupee, TrendingUp, Wallet, Smartphone, Users, Loader2, HandCoins, MapPin, Armchair, Clock,
} from "lucide-react";

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

export default function PartnerDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard/partner").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <DashboardShell title="Partner Dashboard"><div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin gold-text" /></div></DashboardShell>;

  return (
    <DashboardShell title="Partner Dashboard">
      <div className="mb-6">
        <div className="overline-label">Transparent Business Reporting</div>
        <h1 className="mt-1 font-serif text-2xl font-bold">Revenue & Settlement Overview</h1>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi icon={TrendingUp} label="Total Revenue" value={fmt(d.total_revenue)} accent />
        <Kpi icon={Users} label="Total Sessions" value={d.total_sessions} />
        <Kpi icon={HandCoins} label="Discount Given" value={fmt(d.discount_given || 0)} />
        <Kpi icon={Wallet} label="Cash Revenue" value={fmt(d.cash_revenue)} />
      </div>

      {/* Partner split */}
      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <SplitCard name={d.partner_a_name} pct={d.partner_a_pct} share={d.partner_a_share} />
        <SplitCard name={d.partner_b_name} pct={d.partner_b_pct} share={d.partner_b_share} />
      </div>

      <Tabs defaultValue="settlements" className="mt-8">
        <TabsList className="bg-card">
          <TabsTrigger value="settlements">Settlements</TabsTrigger>
          <TabsTrigger value="trends">Trends</TabsTrigger>
          <TabsTrigger value="breakdown">Breakdown</TabsTrigger>
          <TabsTrigger value="staff">Staff</TabsTrigger>
        </TabsList>

        <TabsContent value="settlements" className="mt-5">
          <div className="grid gap-4 md:grid-cols-3">
            {["daily", "weekly", "monthly"].map((k) => (
              <SettlementCard key={k} title={k} s={d.settlements[k]} a={d.partner_a_name} b={d.partner_b_name} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="trends" className="mt-5">
          <Panel title="Revenue — Last 30 Days">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={d.revenue_by_day}>
                <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#E2B857" stopOpacity={0.4} /><stop offset="100%" stopColor="#E2B857" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                <XAxis dataKey="date" tick={{ fill: "#71717A", fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fill: "#71717A", fontSize: 11 }} />
                <Tooltip contentStyle={tt} formatter={(v) => fmt(v)} />
                <Area type="monotone" dataKey="revenue" stroke="#E2B857" strokeWidth={2} fill="url(#g)" />
              </AreaChart>
            </ResponsiveContainer>
          </Panel>
          <Panel title="Revenue by Hour (Peak Demand)" className="mt-5">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={d.revenue_by_hour}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                <XAxis dataKey="hour" tick={{ fill: "#71717A", fontSize: 11 }} tickFormatter={(h) => `${h}:00`} />
                <YAxis tick={{ fill: "#71717A", fontSize: 11 }} />
                <Tooltip contentStyle={tt} formatter={(v) => fmt(v)} labelFormatter={(h) => `${h}:00`} />
                <Bar dataKey="revenue" radius={[4, 4, 0, 0]}>
                  {d.revenue_by_hour.map((e) => <Cell key={e.hour} fill="#E2B857" fillOpacity={0.5 + 0.5 * (e.revenue / Math.max(1, ...d.revenue_by_hour.map((x) => x.revenue)))} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Panel>
        </TabsContent>

        <TabsContent value="breakdown" className="mt-5">
          <div className="grid gap-5 md:grid-cols-2">
            <Panel title="Revenue by Location" icon={MapPin}><BreakdownList items={d.revenue_by_location} /></Panel>
            <Panel title="Revenue by Chair" icon={Armchair}><BreakdownList items={d.revenue_by_chair} /></Panel>
            <Panel title="Revenue by Duration" icon={Clock}><BreakdownList items={d.revenue_by_duration} /></Panel>
            <Panel title="Payment Mix" icon={Smartphone}>
              <BreakdownList items={[
                { name: "Cash", revenue: d.cash_revenue }, { name: "UPI", revenue: d.upi_revenue }, { name: "Online", revenue: d.online_revenue },
              ]} />
            </Panel>
          </div>
        </TabsContent>

        <TabsContent value="staff" className="mt-5">
          <Panel title="Staff Performance">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border text-left text-muted-foreground">
                  <th className="pb-2">Staff</th><th className="pb-2">Sessions</th><th className="pb-2">Cash Confirms</th><th className="pb-2 text-right">Revenue Assisted</th>
                </tr></thead>
                <tbody>
                  {d.staff_performance.map((s) => (
                    <tr key={s.name} className="border-b border-border/50">
                      <td className="py-2.5 font-medium">{s.name}</td><td>{s.sessions}</td><td>{s.cash_confirmations}</td>
                      <td className="text-right gold-text font-semibold">{fmt(s.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </TabsContent>
      </Tabs>
    </DashboardShell>
  );
}

const tt = { background: "#141418", border: "1px solid #27272A", borderRadius: 8, color: "#F9F6F0" };

function Kpi({ icon: Icon, label, value, accent }) {
  return (
    <div className={`rounded-2xl p-5 ${accent ? "bg-gold-muted gold-border" : "bg-card border border-border"}`}>
      <Icon className="gold-text" size={20} />
      <div className="mt-3 text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-bold">{value}</div>
    </div>
  );
}
function SplitCard({ name, pct, share }) {
  return (
    <div className="rounded-2xl bg-card p-6 gold-border">
      <div className="flex items-center justify-between">
        <div><div className="text-xs text-muted-foreground">Partner Share</div><div className="mt-0.5 font-serif text-lg font-bold">{name}</div></div>
        <span className="rounded-full bg-gold-muted px-3 py-1 text-sm font-bold gold-text">{pct}%</span>
      </div>
      <div className="mt-4 flex items-center text-3xl font-bold gold-text"><IndianRupee size={22} />{Number(share).toLocaleString("en-IN")}</div>
    </div>
  );
}
function SettlementCard({ title, s, a, b }) {
  return (
    <div className="rounded-2xl bg-card p-6 gold-border" data-testid={`settlement-${title}`}>
      <div className="overline-label capitalize">{title} Settlement</div>
      <div className="mt-4 space-y-2.5 text-sm">
        <Line l="Gross Revenue" v={fmt(s.gross)} bold />
        <Line l={a} v={fmt(s.partner_a)} />
        <Line l={b} v={fmt(s.partner_b)} />
        <Line l="Adjustments" v={fmt(s.adjustments)} />
        <div className="mt-2 border-t border-border pt-2"><Line l="Final Settlement" v={fmt(s.final)} gold /></div>
      </div>
    </div>
  );
}
function Line({ l, v, bold, gold }) {
  return <div className="flex justify-between"><span className="text-muted-foreground">{l}</span><span className={`${gold ? "gold-text font-bold" : bold ? "font-semibold" : "font-medium"}`}>{v}</span></div>;
}
function Panel({ title, icon: Icon, children, className = "" }) {
  return (
    <div className={`rounded-2xl bg-card p-6 gold-border ${className}`}>
      <div className="mb-4 flex items-center gap-2 font-serif text-lg font-semibold">{Icon && <Icon size={18} className="gold-text" />}{title}</div>
      {children}
    </div>
  );
}
function BreakdownList({ items }) {
  const max = Math.max(1, ...items.map((i) => i.revenue));
  return (
    <div className="space-y-3">
      {items.map((i) => (
        <div key={i.name}>
          <div className="flex justify-between text-sm"><span>{i.name}</span><span className="gold-text font-semibold">{fmt(i.revenue)}</span></div>
          <div className="mt-1 h-1.5 rounded-full bg-secondary"><div className="h-full rounded-full bg-gold" style={{ width: `${(i.revenue / max) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
}
